import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Progress,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { Link } from 'react-router-dom';
import type { RemnantsManageRow } from '../../../api/RemnantsManage';
import {
  isQuarantineActive,
  rowMpView,
} from '../fbs/fbsDisplayHelpers';
import { GoodsFbsActions } from '../fbs/GoodsFbsActions';
import { GoodsFbsStockTable } from '../fbs/GoodsFbsStockTable';

const { Text } = Typography;

type Props = {
  article: string;
  rows: RemnantsManageRow[];
  isCombo: boolean;
  fbsEnabled: boolean;
  pending?: boolean;
  onRecalculated: () => void | Promise<void>;
  onEditFbs: () => void;
  onEditCombo: () => void;
};

export const StocksTab = ({
  article,
  rows,
  isCombo,
  fbsEnabled,
  pending,
  onRecalculated,
  onEditFbs,
  onEditCombo,
}: Props) => {
  const [mpFilter, setMpFilter] = useState<string | 'all'>('all');

  const marketplaces = useMemo(() => {
    const set = new Set(
      rows.map((r) => r.marketplace).filter(Boolean) as string[],
    );
    return [...set];
  }, [rows]);

  const filtered =
    mpFilter === 'all'
      ? rows
      : rows.filter((r) => r.marketplace === mpFilter);

  const available = useMemo(() => {
    const vals = filtered
      .map((r) => Number(r.available_remnant))
      .filter((n) => !Number.isNaN(n));
    return vals.length ? Math.max(...vals) : 0;
  }, [filtered]);

  const allocated = useMemo(() => {
    return filtered.reduce((sum, r) => {
      const view = rowMpView(r, r.marketplace);
      const target = Number(view.target ?? 0);
      return sum + (Number.isNaN(target) ? 0 : target);
    }, 0);
  }, [filtered]);

  const statusTags = useMemo(() => {
    const tags: React.ReactNode[] = [];
    if (!fbsEnabled) {
      tags.push(
        <Tag key="fbs-off" color="default">
          FBS выключен
        </Tag>,
      );
    }
    if (isCombo) {
      tags.push(
        <Tag key="combo" color="purple">
          комбо
        </Tag>,
      );
    }
    for (const row of rows) {
      const view = rowMpView(row, row.marketplace);
      if (view.blocked) {
        tags.push(<Tag key={`block-${row.mp_warehouse_id}`} color="red">блок</Tag>);
        break;
      }
    }
    for (const row of rows) {
      if (row.in_sale === false) {
        tags.push(<Tag key="off-sale" color="volcano">снят с продажи</Tag>);
        break;
      }
    }
    for (const row of rows) {
      const view = rowMpView(row, row.marketplace);
      if (isQuarantineActive(view.quarantine_at)) {
        tags.push(<Tag key="quarantine" color="orange">карантин</Tag>);
        break;
      }
    }
    for (const row of rows) {
      if (!row.mp_linked) {
        tags.push(<Tag key="no-link">нет связи с МП</Tag>);
        break;
      }
    }
    return tags;
  }, [fbsEnabled, isCombo, rows]);

  return (
    <div>
      {!fbsEnabled && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="FBS не включён для этого товара"
          description="Включите «Включено в FBS / распределение» в редактировании карточки, чтобы создались строки остатков на складах МП."
          action={
            <Button size="small" onClick={onEditFbs}>
              Включить FBS
            </Button>
          }
        />
      )}

      <Space
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <Space wrap align="start">
          <Select
            style={{ width: 200 }}
            value={mpFilter}
            onChange={setMpFilter}
            options={[
              { value: 'all', label: 'Все маркетплейсы' },
              ...marketplaces.map((m) => ({ value: m, label: m })),
            ]}
          />
          <div>
            <Text type="secondary">
              Доступно: <Text strong>{available}</Text> шт.
            </Text>
            <div style={{ minWidth: 180 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                К отправке: {allocated} из {available}
              </Text>
              <Progress
                percent={
                  available > 0
                    ? Math.min(100, Math.round((allocated / available) * 100))
                    : 0
                }
                size="small"
                showInfo={false}
              />
            </div>
          </div>
          {statusTags.length > 0 && (
            <Space size={[4, 4]} wrap>
              {statusTags}
            </Space>
          )}
        </Space>
        <Space wrap>
          <Button onClick={onEditCombo}>
            {isCombo ? 'Настроить комбо' : 'Создать комбо'}
          </Button>
          <Button onClick={onEditFbs}>Редактировать FBS</Button>
          <Link to={`/remnants/manage?search=${encodeURIComponent(article)}`}>
            <Button type="link">Полное управление FBS →</Button>
          </Link>
        </Space>
      </Space>

      <div style={{ marginBottom: 16 }}>
        <GoodsFbsActions article={article} onDone={onRecalculated} />
      </div>

      <GoodsFbsStockTable
        rows={filtered}
        isCombo={isCombo}
        pending={pending}
        onRecalculated={onRecalculated}
      />
    </div>
  );
};
